import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AuthLayout, Field, inputClass, primaryButtonClass } from "@/components/AuthLayout";
import { isDemoMode, isLocalSupabase, supabase } from "@/lib/supabase";

interface LoginSearch {
  redirect?: string | undefined;
  mode?: "signin" | "signup" | undefined;
  /** Prefilled from an invitation link. */
  email?: string | undefined;
  /** Invitation link token (ADR-021): sent with the sign-up, or accepted after sign-in. */
  invite?: string | undefined;
}

export const Route = createFileRoute("/login")({
  validateSearch: (raw: Record<string, unknown>): LoginSearch => ({
    // Only same-origin paths, never absolute URLs (open-redirect guard).
    redirect: typeof raw.redirect === "string" && raw.redirect.startsWith("/") && !raw.redirect.startsWith("//") ? raw.redirect : undefined,
    mode: raw.mode === "signup" ? "signup" : undefined,
    email: typeof raw.email === "string" && raw.email.length <= 254 ? raw.email : undefined,
    invite: typeof raw.invite === "string" && /^[0-9a-f]{64}$/.test(raw.invite) ? raw.invite : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    if (isDemoMode) throw redirect({ to: "/" });
    if (await context.auth.getSession()) throw redirect({ to: search.redirect ?? "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const signUp = search.mode === "signup";

  const [email, setEmail] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      if (signUp) {
        const { data, error: err } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              locale: i18n.language,
              ...(search.invite ? { invitation_token: search.invite } : {}),
            },
          },
        });
        if (err) throw err;
        if (!data.session) {
          setNotice(t("auth.checkEmail"));
          return;
        }
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        if (search.invite) {
          // Existing account opening an invitation link: join that organization
          // and switch to it; the refreshed token carries its tenant claim.
          const { data: tenantId, error: acceptError } = await supabase.rpc("accept_invitation", { p_token: search.invite });
          if (acceptError) throw new Error(t("auth.invitationInvalid"));
          const { error: switchError } = await supabase.rpc("switch_tenant", { p_tenant_id: tenantId as string });
          if (switchError) throw switchError;
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) throw refreshError;
          queryClient.clear();
        }
      }
      // Guards re-run with the new session: /onboarding if no organization yet.
      await router.invalidate();
      await navigate({ to: search.redirect ?? "/" });
    } catch (err) {
      const message = (err as { message?: string } | null)?.message ?? "";
      setError(
        /invitation only/i.test(message) ? t("auth.inviteOnly") : err instanceof Error && message ? message : t("auth.errorGeneric"),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout
      title={signUp ? t("auth.signUpTitle") : t("auth.signInTitle")}
      subtitle={signUp ? t("auth.signUpSubtitle") : t("auth.signInSubtitle")}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate={false}>
        {signUp && (
          <Field id="full-name" label={t("auth.fullName")}>
            <input
              id="full-name"
              autoComplete="name"
              required
              autoFocus
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={inputClass}
            />
          </Field>
        )}
        <Field id="email" label={t("auth.email")}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus={!signUp}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field id="password" label={t("auth.password")} {...(signUp ? { hint: t("auth.passwordHint") } : {})}>
          <input
            id="password"
            type="password"
            autoComplete={signUp ? "new-password" : "current-password"}
            required
            minLength={signUp ? 8 : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-lg bg-(--status-danger-bg) px-3 py-2 text-[13px] text-(--status-danger-fg)">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="rounded-lg bg-(--status-success-bg) px-3 py-2 text-[13px] text-(--status-success-fg)">
            {notice}
          </p>
        )}

        <button type="submit" disabled={pending} className={primaryButtonClass}>
          {pending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          {signUp ? t("auth.signUp") : t("auth.signIn")}
        </button>

        {signUp ? (
          <p className="text-center text-[13px] text-ink-muted">
            {t("auth.haveAccount")}{" "}
            <button
              type="button"
              onClick={() => void navigate({ search: (s) => ({ ...s, mode: undefined }), replace: true })}
              className="font-medium text-accent-ink hover:underline"
            >
              {t("auth.switchToSignIn")}
            </button>
          </p>
        ) : (
          // No public sign-up (ADR-021): accounts exist only by the administrator's decision.
          <p className="text-center text-[13px] text-ink-muted">{t("auth.inviteOnlyNote")}</p>
        )}

        {isLocalSupabase && !signUp && (
          <p className="rounded-lg bg-surface-muted px-3 py-2 text-center font-mono text-xs text-ink-muted">{t("auth.localHint")}</p>
        )}
      </form>
    </AuthLayout>
  );
}
